import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'react-hot-toast';

const Sites = () => {
  const { isAuthenticated, loading: authLoading, isAdminMaster } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState(null);

  const [creating, setCreating] = useState(false);
  const [newSite, setNewSite] = useState({
    code: '',
    name: '',
    city: '',
    state: '',
    country: 'USA',
    active: true
  });

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    code: '',
    name: '',
    city: '',
    state: '',
    country: 'USA',
    active: true
  });

  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Helper function to get headers
  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadSites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data } = await axios.get('/sites/', { headers: getHeaders() });
      setSites(data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated()) return navigate('/login');
      if (!isAdminMaster()) {
        setError(t('sites.messages.adminMasterOnly'));
        setLoading(false);
        return;
      }
      loadSites();
    }
  }, [authLoading, isAuthenticated, isAdminMaster, navigate, t]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSite.code || !newSite.name) {
      return toast.error(t('sites.messages.requiredFields'));
    }
    
    try {
      setCreating(true);
      const { data } = await axios.post('/sites/', newSite, { headers: getHeaders() });
      setSites((prev) => [data, ...prev]);
      setNewSite({ code: '', name: '', city: '', state: '', country: 'USA', active: true });
      toast.success(t('sites.messages.createSuccess'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('sites.messages.createError'));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (site) => {
    setEditingId(site.id);
    setEditData({
      code: site.code,
      name: site.name,
      city: site.city || '',
      state: site.state || '',
      country: site.country || 'USA',
      active: site.active
    });
    setOpenMenuId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id) => {
    if (!editData.code || !editData.name) {
      return toast.error(t('sites.messages.requiredFields'));
    }

    try {
      const { data } = await axios.put(`/sites/${id}`, editData, { headers: getHeaders() });
      setSites((prev) => prev.map((s) => (s.id === id ? data : s)));
      setEditingId(null);
      toast.success(t('sites.messages.updateSuccess'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('sites.messages.updateError'));
    }
  };

  const toggleActive = async (id) => {
    try {
      const { data } = await axios.put(`/sites/${id}/status`, {}, { headers: getHeaders() });
      setSites((prev) => prev.map((s) => (s.id === id ? { ...s, active: data.active } : s)));
      setOpenMenuId(null);
      toast.success(t('sites.messages.statusSuccess'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('sites.messages.statusError'));
    }
  };

  const removeSite = async (id) => {
    if (!window.confirm(t('sites.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`/sites/${id}`, { headers: getHeaders() });
      setSites((prev) => prev.filter((s) => s.id !== id));
      setOpenMenuId(null);
      toast.success(t('sites.messages.deleteSuccess'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('sites.messages.deleteError'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-52 text-blue-700 font-semibold">
        {t('sites.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-800 rounded-xl p-6 max-w-xl mx-auto mt-8 shadow-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-blue-900">{t('sites.title')}</h1>
        <div className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg font-semibold border-2 border-purple-300">
          👑 Admin Master
        </div>
      </div>
      <p className="text-gray-700 mb-6">{t('sites.subtitle')}</p>

      {/* Formulario de creación */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-800 mb-4">{t('sites.createTitle')}</h2>
        <form className="flex flex-wrap gap-4 items-end" onSubmit={handleCreate}>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('sites.form.code')} <span className="text-red-500">*</span>
            </label>
            <input
              className="w-32 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newSite.code}
              onChange={(e) => setNewSite((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
              placeholder="RB"
              maxLength={10}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('sites.form.name')} <span className="text-red-500">*</span>
            </label>
            <input
              className="w-56 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newSite.name}
              onChange={(e) => setNewSite((p) => ({ ...p, name: e.target.value }))}
              placeholder={t('sites.form.namePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('sites.form.city')}
            </label>
            <input
              className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newSite.city}
              onChange={(e) => setNewSite((p) => ({ ...p, city: e.target.value }))}
              placeholder={t('sites.form.cityPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('sites.form.state')}
            </label>
            <input
              className="w-32 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newSite.state}
              onChange={(e) => setNewSite((p) => ({ ...p, state: e.target.value }))}
              placeholder="TX"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('sites.form.country')}
            </label>
            <input
              className="w-32 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newSite.country}
              onChange={(e) => setNewSite((p) => ({ ...p, country: e.target.value }))}
              placeholder="USA"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="new-active"
              className="rounded border-gray-300 accent-blue-600"
              type="checkbox"
              checked={newSite.active}
              onChange={(e) => setNewSite((p) => ({ ...p, active: e.target.checked }))}
            />
            <label className="text-sm font-semibold text-gray-800" htmlFor="new-active">
              {t('sites.form.active')}
            </label>
          </div>
          <div>
            <button
              className="px-4 py-2 bg-blue-700 text-white font-bold rounded-lg shadow hover:bg-blue-800 transition disabled:opacity-60"
              disabled={creating}
              type="submit"
            >
              {creating ? t('sites.buttons.creating') : t('sites.buttons.create')}
            </button>
          </div>
        </form>
      </div>

      {/* Lista de sitios */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100 relative">
        <h2 className="text-xl font-bold text-blue-800 mb-4">{t('sites.listTitle')}</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                <th className="px-4 py-2 font-bold">{t('sites.table.code')}</th>
                <th className="px-4 py-2 font-bold">{t('sites.table.name')}</th>
                <th className="px-4 py-2 font-bold">{t('sites.table.city')}</th>
                <th className="px-4 py-2 font-bold">{t('sites.table.state')}</th>
                <th className="px-4 py-2 font-bold">{t('sites.table.country')}</th>
                <th className="px-3 py-2 font-bold text-center text-xs">{t('sites.table.active')}</th>
                <th className="px-4 py-2 font-bold">{t('sites.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id} className="even:bg-gray-50">
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <input
                        className="w-24 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.code}
                        onChange={(e) => setEditData((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                        maxLength={10}
                      />
                    ) : (
                      <span className="font-bold text-blue-700">{site.code}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <input
                        className="w-full min-w-[200px] rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.name}
                        onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{site.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <input
                        className="w-full min-w-[120px] rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.city}
                        onChange={(e) => setEditData((p) => ({ ...p, city: e.target.value }))}
                      />
                    ) : (
                      <span className="text-gray-700">{site.city || '-'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <input
                        className="w-20 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.state}
                        onChange={(e) => setEditData((p) => ({ ...p, state: e.target.value }))}
                      />
                    ) : (
                      <span className="text-gray-700">{site.state || '-'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <input
                        className="w-20 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.country}
                        onChange={(e) => setEditData((p) => ({ ...p, country: e.target.value }))}
                      />
                    ) : (
                      <span className="text-gray-700">{site.country}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mx-auto ${
                        site.active ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                      title={site.active ? t('sites.tooltips.active') : t('sites.tooltips.inactive')}
                    ></div>
                  </td>
                  <td className="px-4 py-2">
                    {editingId === site.id ? (
                      <div className="flex gap-1">
                        <button
                          className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded shadow hover:bg-green-700 transition"
                          onClick={() => saveEdit(site.id)}
                        >
                          💾 {t('sites.buttons.save')}
                        </button>
                        <button
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded hover:bg-gray-300 transition"
                          onClick={cancelEdit}
                        >
                          ✕ {t('sites.buttons.cancel')}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <button
                          className="px-3 py-1.5 text-lg font-bold rounded hover:bg-gray-100 transition text-gray-600"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({ x: rect.right, y: rect.bottom });
                            setOpenMenuId(openMenuId === site.id ? null : site.id);
                          }}
                          title={t('sites.tooltips.actions')}
                        >
                          ⋮
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sites.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('sites.messages.noSites')}
            </div>
          )}
        </div>

        {/* Floating dropdown menu */}
        {openMenuId && (
          <>
            <div
              className="fixed inset-0 z-20"
              onClick={() => setOpenMenuId(null)}
            />
            <div
              className="fixed z-30 bg-white border border-gray-300 rounded-lg shadow-2xl min-w-[160px] py-1"
              style={{
                left: `${menuPosition.x}px`,
                top: `${menuPosition.y}px`,
                transform: 'translateX(-100%)'
              }}
            >
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-blue-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  startEdit(sites.find(s => s.id === openMenuId));
                  setOpenMenuId(null);
                }}
              >
                ✏️ {t('sites.buttons.edit')}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 text-yellow-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  toggleActive(openMenuId);
                  setOpenMenuId(null);
                }}
              >
                {sites.find(s => s.id === openMenuId)?.active
                  ? `🔒 ${t('sites.buttons.deactivate')}`
                  : `🔓 ${t('sites.buttons.activate')}`}
              </button>
              <hr className="my-1 border-gray-200" />
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  removeSite(openMenuId);
                  setOpenMenuId(null);
                }}
              >
                🗑️ {t('sites.buttons.delete')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sites;
